// packages/web/src/components/admin/uren/selection-drawer.tsx
import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useBulkApproveWorker, useBulkApproveWorkorder } from "@/api/hours-entries/mutations";
import { Below8hConfirm } from "./below-8h-confirm.tsx";
import { fmtMinutes } from "./summary-rows.tsx";
import type { HoursEntriesAdminWeekResponse } from "@/api/generated/types.gen";

type Group = HoursEntriesAdminWeekResponse["groups"][number];

interface SelectionDrawerProps {
  groups: Group[];
  isoYear: number;
  isoWeek: number;
  onClear: () => void;
}

interface BulkSummary {
  approved: number;
  skipped: number;
  failed: number;
}

export function SelectionDrawer({ groups, isoYear, isoWeek, onClear }: SelectionDrawerProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const bulkWorker = useBulkApproveWorker();
  const bulkWorkorder = useBulkApproveWorkorder();

  if (groups.length === 0) return null;

  const eligible = groups.filter((g) => g.status !== "rejected");
  const todoCount = eligible.reduce(
    (sum, g) => sum + g.lines.filter((l) => l.status === "to_check").length,
    0,
  );
  const totalMinutes = eligible.reduce((sum, g) => sum + g.summary.totalMinutes, 0);
  const has8hAlert = eligible.some((g) => g.summary.below8hDays.length > 0);

  let summary: string;
  if (groups.length === 1) {
    const g = groups[0]!;
    const single = g.lines.filter((l) => l.status === "to_check").length;
    summary =
      g.kind === "worker"
        ? t("admin.uren.drawer.selection-of-worker", {
            name: g.worker.name,
            total: fmtMinutes(g.summary.totalMinutes),
            count: single,
          })
        : t("admin.uren.drawer.selection-of-workorder", {
            number: `${g.workorder.number}${g.crane ? ` × ${g.crane.number}` : ""}`,
            total: fmtMinutes(g.summary.totalMinutes),
            count: single,
          });
  } else {
    summary = t("admin.uren.drawer.selection-of-many", {
      count: groups.length,
      total: fmtMinutes(totalMinutes),
      todo: todoCount,
    });
  }

  async function approveOne(group: Group): Promise<BulkSummary> {
    const res =
      group.kind === "worker"
        ? await bulkWorker.mutateAsync({ workerId: group.worker.id, isoYear, isoWeek })
        : await bulkWorkorder.mutateAsync({
            workorderId: group.workorder.id,
            craneId: group.crane?.id ?? null,
            isoYear,
            isoWeek,
          });
    return {
      approved: res.approved.length,
      skipped: res.skipped.length,
      failed: res.failures.length,
    };
  }

  async function runApprove() {
    setConfirmOpen(false);
    setPending(true);
    try {
      const results = await Promise.allSettled(eligible.map(approveOne));
      const totals = results.reduce<BulkSummary>(
        (acc, r) => {
          if (r.status === "fulfilled") {
            acc.approved += r.value.approved;
            acc.skipped += r.value.skipped;
            acc.failed += r.value.failed;
          } else {
            acc.failed += 1;
          }
          return acc;
        },
        { approved: 0, skipped: 0, failed: 0 },
      );
      toast.success(t("admin.uren.toasts.bulk-approved", { count: totals.approved }), {
        description: t("admin.uren.toasts.bulk-detail", {
          skipped: totals.skipped,
          failed: totals.failed,
        }),
      });
      onClear();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setPending(false);
    }
  }

  function onApproveClick() {
    if (has8hAlert) {
      setConfirmOpen(true);
      return;
    }
    void runApprove();
  }

  function openEdit() {
    const firstTodo = groups[0]!.lines.find((l) => l.status === "to_check");
    const entryId = firstTodo?.entries[0]?.id;
    if (entryId) {
      navigate({ to: "/admin/uren/$entryId/edit", params: { entryId } });
    }
  }

  const canEdit = groups.length === 1;
  const canApprove = todoCount > 0 && eligible.length > 0;

  return (
    <>
      <div className="sticky bottom-0 left-0 right-0 flex flex-wrap items-center justify-between gap-3 border-t border-line bg-surface-1 px-4 py-3 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] md:px-6 md:pb-3">
        <div className="flex flex-col gap-0.5">
          <span className="text-sm text-ink-900">
            {summary}
            {has8hAlert && " ⚠"}
          </span>
          {groups.length > 1 && (
            <button
              type="button"
              onClick={onClear}
              className="self-start text-xs text-ink-500 underline-offset-2 hover:underline"
            >
              {t("admin.uren.drawer.clear-selection")}
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {canEdit && (
            <button
              onClick={openEdit}
              className="px-3 py-1.5 text-sm rounded border border-line bg-surface"
            >
              {t("admin.uren.drawer.edit")}
            </button>
          )}
          <button
            onClick={onApproveClick}
            disabled={pending || !canApprove}
            className="px-3 py-1.5 text-sm rounded bg-p-600 text-white disabled:opacity-60"
          >
            {groups.length > 1
              ? t("admin.uren.drawer.approve-many", {
                  groups: eligible.length,
                  count: todoCount,
                })
              : t("admin.uren.drawer.approve", { count: todoCount })}
          </button>
        </div>
      </div>
      <Below8hConfirm
        open={confirmOpen}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => void runApprove()}
      />
    </>
  );
}
